// stores/historyWayPointStore.js
'use client';

import { useRef, useMemo } from 'react';
import { fleetApi } from '../api/axiosClient';
import useSWR from 'swr';

import dayjs from 'dayjs';
import { timezone } from '../utils/timeZone';

const getTz = (tz) => {
  if (tz !== undefined && tz !== null && !isNaN(tz)) return Number(tz);
  const defaultTz = typeof timezone === 'function' ? timezone() : Number(timezone);
  return !isNaN(defaultTz) ? defaultTz : Math.abs(new Date().getTimezoneOffset() / 60);
};
const fetcher = async ([urlPath, payload = {}, queryParams = {}]) => {
  try {
    const qpObj = { ...(queryParams || {}) };
    if (qpObj.tz == null) qpObj.tz = getTz();
    const qp = new URLSearchParams(qpObj).toString();
    const fullUrl = qp ? `${urlPath}?${qp}` : urlPath;
    const response = await fleetApi.post(fullUrl, payload);
    return response.data;
  } catch (err) {
    console.error('API fetch error History Waypoint:', {
      urlPath,
      payload,
      status: err?.response?.status,
      responseData: err?.response?.data,
      message: err?.message
    });
    throw err;
  }
};

const getItemKey = (it) => {
  if (!it) return '';
  if (it.id != null) return String(it.id);
  if (it.raw && it.raw.id != null) return String(it.raw.id);
  if (it.vehicle_id != null) return String(it.vehicle_id) + '|' + String(it.start_time ?? it.startTime ?? '');
  const ep = it.startTimeEpoch ?? it.start_time ?? it.raw?.start_time ?? it.raw?.date_send_epoch ?? null;
  if (ep != null) return String(ep);
  try {
    return JSON.stringify({ v: it.vehicle_id ?? it.raw?.vehicle_id ?? '', s: it.startTimeEpoch ?? '' }).slice(0, 80);
  } catch {
    return Math.random().toString(36).slice(2, 9);
  }
};

export const useHistoryWayPointList = (
  vehicleId = null,
  companyId = null,
  type = null,
  startTime = null,
  endTime = null,
  dateError,
  onProgress,
  tz = null
) => {
  const activeTz = getTz(tz);
  let resolvedCompanyId = companyId;
  if (!resolvedCompanyId) {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        resolvedCompanyId = u.company ?? u.company_id ?? resolvedCompanyId;
      }
    } catch (err) {
      console.warn(err);
    }
  }
  const shouldFetch = Boolean(vehicleId && startTime && endTime && !dateError);
  const fetchAllForType = async (singleType = null, progressCb = null) => {
    let allData = [];

    const dailyRanges = [];
    if (startTime && endTime) {
      const searchStart = dayjs(startTime);
      const searchEnd = dayjs(endTime);
      let curr = searchStart.clone().startOf('day');
      const end = searchEnd.clone().startOf('day');

      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        const currentLoopDay = curr.clone();
        let loopStartTime = currentLoopDay.startOf('day');
        let loopEndTime = currentLoopDay.endOf('day');

        if (currentLoopDay.isSame(searchStart, 'day')) loopStartTime = searchStart;
        if (currentLoopDay.isSame(searchEnd, 'day')) loopEndTime = searchEnd;

        dailyRanges.push({
          startStr: loopStartTime.subtract(activeTz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
          endStr: loopEndTime.subtract(activeTz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]')
        });

        curr = curr.add(1, 'day');
      }
    } else {
      dailyRanges.push({ startStr: startTime, endStr: endTime });
    }

    for (const range of dailyRanges) {
      const pageSize = 100;
      let start = 0;
      let hasMore = true;

      const baseColumns = [];
      if (range.startStr && range.endStr) {
        baseColumns.push({
          logic_operator: 'RANGE',
          name: 'wp_time',
          operator: 'AND',
          value: range.startStr,
          value1: range.endStr
        });
      }

      const queryParams = {
        vehicleId,
        companyId: resolvedCompanyId,
        tz: activeTz
      };
      if (singleType) queryParams.type = singleType;

      while (hasMore) {
        const payload = {
          paging: { start: start, length: pageSize },
          columns: baseColumns
        };

        try {
          const response = await fetcher(['/history-waypoint/search', payload, queryParams]);
          const batchData = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
          if (batchData.length === 0) {
            hasMore = false;
          } else {
            allData = [...allData, ...batchData];
            if (batchData.length < pageSize) {
              hasMore = false;
            } else {
              start += pageSize;
            }
          }
        } catch (e) {
          console.warn(`Error fetching waypoint for type ${singleType} in range`, range, e);
          break; // break out of the while loop for this day on error
        }

        if (start > 50000) hasMore = false;
      }
      if (progressCb) progressCb();
    }

    return { data: allData, totalRecords: allData.length };
  };
  const fetchAllData = async () => {
    let completedSteps = 0;

    let dailyRangesLength = 1;
    if (startTime && endTime) {
      const searchStart = dayjs(startTime);
      const searchEnd = dayjs(endTime);
      let curr = searchStart.clone().startOf('day');
      const end = searchEnd.clone().startOf('day');
      let count = 0;
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        count++;
        curr = curr.add(1, 'day');
      }
      dailyRangesLength = count > 0 ? count : 1;
    }

    let totalSteps = dailyRangesLength;
    if (Array.isArray(type) && type.length > 0) totalSteps = type.length * dailyRangesLength;

    const notifyProgress = () => {
      completedSteps++;
      if (onProgress) {
        onProgress(Math.min(100, Math.round((completedSteps / totalSteps) * 100)));
      }
    };

    if (onProgress) onProgress(0);

    if (type == null) {
      const res = await fetchAllForType(null, notifyProgress);
      return { data: res.data, totalRecords: res.totalRecords ?? res.data.length ?? 0 };
    }
    if (typeof type === 'string') {
      const res = await fetchAllForType(type, notifyProgress);
      return { data: res.data, totalRecords: res.totalRecords ?? res.data.length ?? 0 };
    }
    if (Array.isArray(type) && type.length > 0) {
      try {
        const results = await Promise.all(type.map((singleType) => fetchAllForType(singleType || null, notifyProgress)));
        const combinedData = results.flatMap((res) => res.data);
        const totalRecords = results.reduce((acc, res) => acc + (res.totalRecords || 0), 0);
        return { data: combinedData, totalRecords };
      } catch (err) {
        console.error('Error fetching multiple history types:', err);
        throw err;
      }
    }
    return { data: [], totalRecords: 0 };
  };

  const key = shouldFetch ? ['history-waypoint-list', vehicleId, resolvedCompanyId, type, startTime, endTime, activeTz] : null;

  const { data, error } = useSWR(key, fetchAllData, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rawArr = data?.data ?? data ?? [];
  const prevDataRef = useRef(null);

  const stableData = useMemo(() => {
    if (!Array.isArray(rawArr)) {
      prevDataRef.current = [];
      return [];
    }
    if (Array.isArray(prevDataRef.current) && prevDataRef.current.length === rawArr.length) {
      let same = true;
      for (let i = 0; i < rawArr.length; i++) {
        try {
          const aKey = getItemKey(rawArr[i]);
          const bKey = getItemKey(prevDataRef.current[i]);
          if (aKey !== bKey) {
            same = false;
            break;
          }
        } catch (e) {
          console.warn(e);
          same = false;
          break;
        }
      }
      if (same) {
        return prevDataRef.current;
      }
    }
    prevDataRef.current = Array.isArray(rawArr) ? rawArr : [];
    return prevDataRef.current;
  }, [rawArr]);

  return {
    data: stableData,
    totalRecords: data?.totalRecords ?? 0,
    isLoading: shouldFetch && !data && !error,
    isError: error
  };
};
